import { useMemo, useRef, useState } from 'react';

import { cannotPlayEncoded, hasNoPicture, isUnplayableMediaError } from '@/utils/video-playback';

type Resume = { time: number; playing: boolean };

/**
 * Which rendition of a backend video to play: the main file, or its `backup`
 * (H.264 + AAC MP4) when this browser cannot play the main one.
 *
 * The backup is chosen up front when `canPlayType` rules the main codec out,
 * and otherwise after the fact — once, never back — when the element either
 * fails on the format (`onUnplayable`, `onError`) or reads metadata with no
 * picture (`onLoadedMetadata`). The playhead and play state carry over.
 *
 * It only picks the link: the caller feeds `link` to the same loader it used
 * for the main file, so a streamed link stays streamed and signed and a blob
 * stays a blob, and auth or processing failures of either rendition are still
 * that loader's to handle. With no backup this is a pass-through.
 */
export const useBackupVideo = (
  link: null | string | undefined,
  { backup, contentType }: { backup?: null | string; contentType?: null | string },
) => {
  const [failedLink, setFailedLink] = useState<null | string>(null);
  const resume = useRef<null | Resume>(null);

  const unsupported = useMemo(
    () => !!backup && cannotPlayEncoded(contentType),
    [backup, contentType],
  );
  const usingBackup = !!link && !!backup && (unsupported || failedLink === link);
  const canSwitch = !!link && !!backup && !usingBackup;

  /** Switches to the backup if there is one left to switch to; says whether it did. */
  const onUnplayable = (media: HTMLMediaElement) => {
    if (!canSwitch) return false;
    resume.current = { time: media.currentTime, playing: !media.paused };
    setFailedLink(link);
    return true;
  };

  /** For a source that cannot fail on auth or processing — a blob it already holds. */
  const onError = (event: React.SyntheticEvent<HTMLMediaElement>) => {
    const media = event.currentTarget;
    return isUnplayableMediaError(media.error) && onUnplayable(media);
  };

  /** True when it switched away, and the rest of the caller's metadata handling is moot. */
  const onLoadedMetadata = (event: React.SyntheticEvent<HTMLVideoElement>) => {
    const video = event.currentTarget;
    if (hasNoPicture(video) && onUnplayable(video)) return true;

    const pending = resume.current;
    if (!pending || !usingBackup) return false;
    resume.current = null;
    if (pending.time > 0) video.currentTime = pending.time;
    if (pending.playing) void video.play();
    return false;
  };

  return {
    link: usingBackup ? backup : link,
    usingBackup,
    onUnplayable: canSwitch ? onUnplayable : undefined,
    onError,
    onLoadedMetadata,
  };
};
